import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styles from "./Reports.module.css";

import ReportHeader from "../../components/ReportHeader";
import InfoCard from "../../components/InfoCard";

import { ReportDetails } from "../../components/ReportDetails/ReportDetails";

import { useReportsGet } from "./useReportsGet";
import Loader from "components/Loader";
import { useAppDataContext } from "../../App";
import { useTranslation } from "react-i18next";

export const Reports = () => {
  const {t} = useTranslation();
  const location = useLocation();
  const { iboNumber, isLoading: isIboLoading } = useAppDataContext();
  const { data: reportsData, isLoading, refetch } = useReportsGet();

  const defaultIbo = {
    ibo: "1478107",
    iboName: "Randy & Raye-Lynn Jassman",
  };

  const [allIbos, setAllIbos] = useState(null);
  const [currentIbo, setCurrentIbo] = useState(null);

  useEffect(() => {
    if (iboNumber && !allIbos) {
      const saved = localStorage.getItem("allIbos");

      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Ensure it's a non-empty array with objects containing "ibo"
          if (Array.isArray(parsed) && parsed.every((p) => p?.ibo)) {
            setAllIbos(parsed);
            setCurrentIbo(parsed[parsed.length - 1]);
          } else {
            throw new Error("Invalid allIbos structure");
          }
        } catch (e) {
          console.warn("Failed to parse allIbos from localStorage:", e);
          const initialIbo = {
            ibo: iboNumber,
            iboName: "",
          };
          setAllIbos([initialIbo]);
          setCurrentIbo(initialIbo);
        }
      } else {
        const initialIbo = {
          ibo: iboNumber,
          iboName: "",
        };
        setAllIbos([initialIbo]);
        setCurrentIbo(initialIbo);
      }
    }
  }, [iboNumber, allIbos]);

  useEffect(() => {
    if (allIbos) {
      localStorage.setItem("allIbos", JSON.stringify(allIbos));
    }
  }, [allIbos]);

  const [selected, setSelected] = useState(false);
  const [selectedHeader, setSelectedHeader] = useState(null);
  const [reports, setReports] = useState(null);
  const [initialContext, setInitialContext] = useState(null);

  // Helper to reset filters and IBO stack
  const resetReportState = () => {
    setSelected(false);
    setSelectedHeader(null);
    setInitialContext(null);
    if (iboNumber) {
      setAllIbos([{ ibo: iboNumber, iboName: "" }]);
      setCurrentIbo({ ibo: iboNumber, iboName: "" });
    }
    localStorage.removeItem("allIbos");
  };

  useEffect(() => {
    if (
      location.state &&
      location.state.reportStack &&
      location.state.reportStack.length > 0
    ) {
      const stack = [...location.state.reportStack];
      const context = stack.pop();
      if (context) {
        setSelected(context.reportType);
        setSelectedHeader(context.header);
        setInitialContext(context);
      }
      // Optional
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (reportsData) {
      setReports(reportsData);
    }
  }, [reportsData]);

  if (isLoading || isIboLoading || !reports || !currentIbo || !allIbos) {
    return (
      <div className={styles.loaderContainer}>
        <Loader color="#22252e" />
      </div>
    );
  }

  const handleIboNumChange = (newIboObj) => {
    if (newIboObj.ibo === currentIbo.ibo) return;

    const updated = [...allIbos, newIboObj];
    setAllIbos(updated);
    setCurrentIbo(newIboObj);
  };

  const handleIboBack = () => {
    if (allIbos.length <= 1) return;

    const updated = allIbos.slice(0, -1);
    setAllIbos(updated);
    setCurrentIbo(updated[updated.length - 1]);
  };

  return (
    <div className={styles.container}>
      {!selected && (
        <div className={styles.cardContainer}>
          <ReportHeader header={t("Membership (Monthly)")} />
          <InfoCard
            header={t("Premier Membership")}
            icon={reports.membership.digitalDelivery.logo}
            people={reports.membership.digitalDelivery.value}
            trend={reports.membership.digitalDelivery.trend}
            percent={reports.membership.digitalDelivery.trendPercentage}
            percentDate={reports.membership.digitalDelivery.trendDate}
            date={reports.membership.digitalDelivery.date}
            background={"243746"}
            onClick={() => {
              resetReportState();
              setSelected("membership");
              setSelectedHeader("Premier Membership");
            }}
          />
          <InfoCard
            header={"Digital Delivery"}
            icon={reports.membership.premierMembership.logo}
            people={reports.membership.premierMembership.value}
            trend={reports.membership.premierMembership.trend}
            percent={reports.membership.premierMembership.trendPercentage}
            percentDate={reports.membership.premierMembership.trendDate}
            date={reports.membership.premierMembership.date}
            background={"415364"}
            onClick={() => {
              resetReportState();
              setSelected("membership");
              setSelectedHeader("Digital Delivery");
            }}
          />

          <ReportHeader header={t("Events (Majors)")} />
          {reports.events.majors.map((majorEvent) => (
            <InfoCard
              header={majorEvent.eventName}
              icon={majorEvent.logo}
              people={majorEvent.value}
              trend={majorEvent.trendLastMajor}
              trendLast={majorEvent.trendLastEvent}
              date={majorEvent.date}
              background={"91B6BB"}
              onClick={() => {
                resetReportState();
                setSelected("major");
                setSelectedHeader(majorEvent.eventName);
              }}
            />
          ))}
          <ReportHeader header={t("Events (Locals)")} />
          {reports.events.locals.map((localEvent) => (
            <InfoCard
              header={localEvent.eventName}
              icon={localEvent.logo}
              people={localEvent.value}
              trend={localEvent.trend}
              percent={localEvent.trendPercentage}
              percentDate={localEvent.trendDate}
              date={localEvent.date}
              background={"ADB7C0"}
              onClick={() => setSelected(true)}
            />
          ))}

          <ReportHeader header={"Topics"} />
          <div className={styles.finalItem}>
            <InfoCard
              header={"Topics"}
              icon={reports.topics.logo}
              people={reports.topics.value}
              trend={reports.topics.trend}
              percent={reports.topics.trendPercentage}
              percentDate={reports.topics.trendDate}
              date={reports.topics.date}
              background={"9E703C"}
              onClick={() => setSelected(true)}
            />
          </div>
        </div>
      )}

      {selected && (
        <div>
          <ReportDetails
            onBack={() => {
              resetReportState();
            }}
            ibo={currentIbo.ibo}
            previousIbo={
              allIbos.length > 1 ? allIbos[allIbos.length - 2] : null
            }
            reportType={selected}
            header={selectedHeader}
            changeIbo={handleIboNumChange}
            onIboBack={handleIboBack}
            setCurrentIboName={(iboName) => {
              setAllIbos((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  ...updated[updated.length - 1],
                  iboName,
                };
                return updated;
              });

              setCurrentIbo((prev) => ({
                ...prev,
                iboName,
              }));
            }}
            initialContext={initialContext}
          />
        </div>
      )}
    </div>
  );
};
